import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { CreditCard, CheckCircle2, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { stripeConnectAPI } from '../../lib/api';

export default function StripeConnectPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async (live = false) => {
    try {
      const r = await stripeConnectAPI.getStatus(live);
      setStatus(r.data);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // If returning from Stripe onboarding, pull a live status refresh.
    const params = new URLSearchParams(window.location.search);
    load(params.get('stripe_return') === '1');
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await stripeConnectAPI.connect();
      if (r.data?.url) { window.location.href = r.data.url; return; }
      toast.error('Could not start Stripe onboarding');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Online payments are not configured');
    } finally { setConnecting(false); }
  };

  if (loading) return null;

  const connected = status?.connected;
  const ready = status?.charges_enabled;

  return (
    <Card data-testid="stripe-connect-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-violet-500" /> Accept Online Payments
        </CardTitle>
        <CardDescription>
          Connect your own Stripe account so clients can pay you by card right when they sign a quote.
          Money goes straight to your account — you keep 100%.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {ready ? (
          <div className="flex items-center gap-2 text-emerald-600" data-testid="stripe-connected-ready">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">Connected — you're ready to accept card payments.</span>
          </div>
        ) : connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-600" data-testid="stripe-onboarding-incomplete">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Almost there — finish your Stripe onboarding to start accepting payments.</span>
            </div>
            <Button onClick={connect} disabled={connecting} data-testid="stripe-continue-onboarding-btn">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Continue Stripe Onboarding
            </Button>
          </div>
        ) : (
          <Button onClick={connect} disabled={connecting} className="bg-violet-600 hover:bg-violet-700" data-testid="stripe-connect-btn">
            {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
            Connect with Stripe
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
